/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useMemo, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
// @ts-expect-error no typings
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import openCallingWidget from 'calling-global-api/methods/openCallingWidget';
import { isUninitialized, isLoading, getData } from 'calling-lifecycle-internal/async-flux-utils/operators/getApiProperties';
import RegisterPhoneNumberPrompt from 'calling-lifecycle-internal/onboarding/components/RegisterPhoneNumberPrompt';
import { getPersistedCallProvider } from 'calling-lifecycle-internal/utils/getLocalCallSettings';
import { getIsProviderHubSpot } from 'calling-lifecycle-internal/call-provider/operators/getIsTwilioBasedCallProvider';
import { getTwilioCallingProvider } from 'calling-lifecycle-internal/call-provider/operators/callProviderOperators';
import getLocalDevelopmentWidgetInfo from 'calling-integration-sdk-support/external-provider/operators/getLocalDevelopmentWidgetInfo';
import { getPhoneNumbers, getConnectAccountNumbers, getHubspotProvidedNumbers, getHubspotSubAccountNumbers, getInstalledCallingApplications, getHubSpotCallingEnabled } from '../../callee-omnibus/operators/settingsOmnibusResultGetters';
import GlobalCallingDisabled from './GlobalCallingDisabled';
import UnlockCallingItem from 'calling-orchestration-shared-library/unlock-calling/components/UnlockCallingItem';
import { CommunicatorLogger } from 'calling-lifecycle-usage-tracker/callingTracker';
import getIn from 'transmute/getIn';
import CallNumberSkeletons from './CallNumberSkeletons';
import { getUserPreferencesUrl } from './ChangeProviderLink';
import { getIsUngatedForBidirectionalProviderSwitching } from '../../calling-gates/utils/getIsUngatedForBidirectionalProviderSwitching';
import { isProviderSwitchingEnabled } from 'calling-internal-common/utils/isProviderSwitchingEnabled';
import { hasPaidSeatAccess } from 'calling-orchestration-shared-library/hub-info/utils/hasCallingFeature';
import { hasCallingMinutesPerPortal } from 'calling-orchestration-shared-library/hub-info/utils/getCallingLimits';
import { jsx as _jsx } from "react/jsx-runtime";
function CallingOnboarding({
  asyncSettingsOmnibus,
  children,
  onClosePopover,
  gates,
  appIdentifier,
  ignoreNonHSCalling = false
}) {
  const isOmnibusDataUninitialized = isUninitialized(asyncSettingsOmnibus);
  const isOmnibusDataLoading = isLoading(asyncSettingsOmnibus);
  const omnibusData = getData(asyncSettingsOmnibus);
  const provider = getPersistedCallProvider();
  const isHubSpotProvider = getIsProviderHubSpot(provider);
  const isUngatedForProviderSwitching = isProviderSwitchingEnabled(gates);
  const phoneNumbers = getPhoneNumbers(omnibusData);
  const hubspotSubAccountNumbers = getHubspotSubAccountNumbers(phoneNumbers);
  const hubspotProvidedNumbers = getHubspotProvidedNumbers(phoneNumbers);
  const twilioAccountNumbers = getConnectAccountNumbers(phoneNumbers);
  const hasHubspotSubAccountOrProvidedNumbers = hubspotSubAccountNumbers && hubspotSubAccountNumbers.size > 0 || hubspotProvidedNumbers && hubspotProvidedNumbers.size > 0;
  const hasTwilioAccountNumbers = twilioAccountNumbers && twilioAccountNumbers.size > 0;
  const installedCallingApplications = getInstalledCallingApplications(omnibusData);
  const connectedAppsWithLocalStorageOverrides = getLocalDevelopmentWidgetInfo(installedCallingApplications);
  const hasThirdPartyCalling = connectedAppsWithLocalStorageOverrides && connectedAppsWithLocalStorageOverrides.size > 0;

  // Scenario 1: HS provider and there are twilio connect numbers available
  // Scenario 2: Has third party calling options
  const shouldShowProviderSelectionLink = useMemo(() => {
    if (ignoreNonHSCalling) return false;
    if (isHubSpotProvider && twilioAccountNumbers.size > 0) {
      return true;
    }
    return hasThirdPartyCalling;
  }, [hasThirdPartyCalling, ignoreNonHSCalling, isHubSpotProvider, twilioAccountNumbers.size]);

  // show when hubspot provider, globally disabled and no other third party/connect options
  const shouldShowCallingDisabled = useMemo(() => !getHubSpotCallingEnabled(omnibusData) && isHubSpotProvider && !hasThirdPartyCalling && twilioAccountNumbers.size === 0, [hasThirdPartyCalling, isHubSpotProvider, omnibusData, twilioAccountNumbers.size]);

  // show when hubspot provider, no calling paid seat and no other third party options
  const shouldShowFreeCalling = useMemo(() => !hasPaidSeatAccess() && isHubSpotProvider && !hasThirdPartyCalling, [hasThirdPartyCalling, isHubSpotProvider]);
  const handleTracking = useCallback(() => {
    CommunicatorLogger.log('calling_callUpgradeClicked', {
      source: appIdentifier
    });
  }, [appIdentifier]);
  const handleProviderSwitch = useCallback(() => {
    let callingProvider = null;
    if (callingProvider && getIsUngatedForBidirectionalProviderSwitching(gates, appIdentifier, callingProvider)) {
      window.open(getUserPreferencesUrl(), '_blank');
      return;
    }
    if (hasTwilioAccountNumbers) {
      callingProvider = getTwilioCallingProvider();
    } else {
      callingProvider = getIn([0], installedCallingApplications);
    }
    if (callingProvider) {
      openCallingWidget({
        callingProvider
      });
    }
    onClosePopover();
  }, [hasTwilioAccountNumbers, installedCallingApplications, onClosePopover, appIdentifier, gates]);
  const handleChangeProvider = () => {
    openCallingWidget({
      showProviderMenu: true
    });
  };

  // show loading state
  if (isOmnibusDataUninitialized || isOmnibusDataLoading) return /*#__PURE__*/_jsx(CallNumberSkeletons, {});
  if (shouldShowCallingDisabled) return /*#__PURE__*/_jsx(GlobalCallingDisabled, {});
  if (shouldShowFreeCalling) {
    if (hasCallingMinutesPerPortal()) {
      return /*#__PURE__*/_jsx(UnlockCallingItem, {
        blockingType: "paid-seat-blocking",
        use: "popover",
        "data-test-id": "paid-seat-blocking-popover"
      });
    }
    return /*#__PURE__*/_jsx(UnlockCallingItem, {
      blockingType: "free-account-blocking",
      use: "popover",
      onTrack: handleTracking,
      "data-test-id": "free-account-blocking-popover"
    });
  }

  // Show this when HS provider selected but no HS phone numbers
  // NOTE: you can show this regardless of existence of 3rd party calling since there is a provider switch link
  if (isHubSpotProvider && !hasHubspotSubAccountOrProvidedNumbers) return /*#__PURE__*/_jsx(RegisterPhoneNumberPrompt, {
    shouldShowProviderSelectionLink: isUngatedForProviderSwitching ? true : shouldShowProviderSelectionLink,
    handleProviderSwitch: isUngatedForProviderSwitching ? handleChangeProvider : handleProviderSwitch
  });
  return children;
}
CallingOnboarding.propTypes = {
  asyncSettingsOmnibus: RecordPropType('AsyncSettingsOmnibusResult').isRequired,
  children: PropTypes.node.isRequired,
  onRegisterFromPhoneNumber: PropTypes.func.isRequired,
  onShowOnboardingIntro: PropTypes.func.isRequired,
  onClosePopover: PropTypes.func.isRequired,
  appIdentifier: PropTypes.string.isRequired
};
export default /*#__PURE__*/memo(CallingOnboarding);